/**
 * The citizen's bell: what is waiting for them, in one number.
 *
 * Two things wait for a citizen — messages they have not read, and what a
 * company asked of them: its invitations and its sponsorship offers (an offer
 * is the invitation to be sponsored, 3 Oct 2026). The bell counts both, so an
 * ask is never only a Home card that one "Not now" takes out of sight. One put
 * off with "Not now" stays listed (the bell's Invitations tab, the phone's
 * Inbox) but leaves the count until its window lapses — the server says when,
 * in `deferredUntil`. The Inbox's own unread number is unchanged.
 *
 * Every part degrades on its own: while one is loading or failing it counts
 * zero, so the bell never loses the parts it does have.
 */
import { useMyInvitations } from './useTalentInvitations';
import { useInboxUnreadCount } from './useCommunications';
import { useMySponsorshipOffers } from './useBilling';

export interface CitizenBellCount {
  /** Unread Inbox messages — the Inbox's one unread number. */
  unread: number;
  /** Pending invitations and sponsorship offers, the ones put off with "Not now" included. */
  invitations: number;
  /** Pending invitations and offers NOT put off — the ones the bell asks about. */
  invitationsWaiting: number;
  /** What the badge shows: unread messages plus the invitations and offers waiting. */
  total: number;
}

export function useCitizenBellCount(): CitizenBellCount {
  const { data: unread = 0 } = useInboxUnreadCount();
  const { data: invitations = [] } = useMyInvitations();
  const { data: offers = [] } = useMySponsorshipOffers();
  const asks = [...invitations, ...offers];
  const invitationsWaiting = asks.filter((a) => a.deferredUntil === null).length;
  return {
    unread,
    invitations: asks.length,
    invitationsWaiting,
    total: unread + invitationsWaiting,
  };
}
